import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Info, X } from 'lucide-react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { fetchWhatsAppAnalytics } from '../../services/MasterAdmin/whatsappCampaignService';
import type { WhatsAppAnalytics } from '../../models/MasterAdmin/whatsappHub';
import { WHATSAPP_BASE_PATH } from '../../models/MasterAdmin/whatsappHub';
import WhatsAppPanelShell from './WhatsAppPanelShell';

function StatCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub ? <p className="mt-1 text-xs text-gray-500">{sub}</p> : null}
    </div>
  );
}

function formatInr(value?: number) {
  return `₹${(value ?? 0).toFixed(3)}`;
}

type BreakdownTab = 'events' | 'templates' | 'campaigns';

const WhatsAppAnalyticsPanel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<WhatsAppAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [breakdownTab, setBreakdownTab] = useState<BreakdownTab>('events');

  const days = Number(searchParams.get('days') || 30);
  const source = searchParams.get('source') || '';
  const eventType = searchParams.get('eventType') || '';
  const templateName = searchParams.get('templateName') || '';
  const messageCategory = searchParams.get('messageCategory') || '';
  const recipientType = searchParams.get('recipientType') || '';
  const focused = searchParams.get('focused') === '1' || Boolean(eventType);

  const filterParams = useMemo(
    () => ({
      days,
      source: source || undefined,
      eventType: eventType || undefined,
      templateName: templateName || undefined,
      messageCategory: messageCategory || undefined,
      recipientType: recipientType || undefined,
    }),
    [days, source, eventType, templateName, messageCategory, recipientType]
  );

  useEffect(() => {
    setLoading(true);
    fetchWhatsAppAnalytics(filterParams)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filterParams]);

  useEffect(() => {
    if (eventType && breakdownTab === 'campaigns') {
      setBreakdownTab('events');
    }
  }, [eventType, breakdownTab]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key === 'eventType' && !value) next.delete('focused');
    setSearchParams(next, { replace: true });
  };

  const clearFocus = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('eventType');
    next.delete('templateName');
    next.delete('focused');
    setSearchParams(next, { replace: true });
  };

  const filterClass =
    'min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs dark:border-gray-600 dark:bg-gray-800 sm:text-sm';

  const templateOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of data?.templateBreakdown || []) {
      if (row.templateName) names.add(row.templateName);
    }
    for (const row of data?.eventBreakdown || []) {
      if (row.templateName) names.add(row.templateName);
    }
    if (templateName) names.add(templateName);
    return Array.from(names).sort();
  }, [data, templateName]);

  const eventOptions = useMemo(() => {
    const names = new Set<string>();
    for (const row of data?.eventBreakdown || []) {
      if (row.eventType) names.add(row.eventType);
    }
    if (eventType) names.add(eventType);
    return Array.from(names).sort();
  }, [data, eventType]);

  const focusedEventRows = useMemo(() => {
    if (!eventType) return data?.eventBreakdown || [];
    return (data?.eventBreakdown || []).filter((row) => row.eventType === eventType);
  }, [data, eventType]);

  const focusedTemplateRows = useMemo(() => {
    if (!eventType && !templateName) return data?.templateBreakdown || [];
    if (templateName) {
      return (data?.templateBreakdown || []).filter((row) => row.templateName === templateName);
    }
    const names = new Set(focusedEventRows.map((r) => r.templateName).filter(Boolean) as string[]);
    if (!names.size) return data?.templateBreakdown || [];
    return (data?.templateBreakdown || []).filter(
      (row) => row.templateName && names.has(row.templateName)
    );
  }, [data, eventType, templateName, focusedEventRows]);

  const chartData = useMemo(
    () =>
      (data?.dailyTrend || []).map((row) => ({
        day: row.day.slice(5),
        fullDay: row.day,
        messages: row.messages,
        cost: Number((row.estimatedCostInr || 0).toFixed(3)),
      })),
    [data]
  );

  const rates = data?.costPerMessageInr || {};

  return (
    <WhatsAppPanelShell>
      {eventType ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#25D366]/30 bg-[#25D366]/5 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Showing only this notification
            </p>
            <p className="mt-0.5 font-mono text-xs text-gray-600 dark:text-gray-300">{eventType}</p>
            {templateName ? <p className="mt-0.5 text-xs text-gray-500">Template: {templateName}</p> : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to={`${WHATSAPP_BASE_PATH}/notifications`}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-900"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to notifications
            </Link>
            <button
              type="button"
              onClick={clearFocus}
              className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-900"
            >
              View all analytics
            </button>
          </div>
        </div>
      ) : null}

      <div className="mb-6 flex flex-nowrap items-center gap-2 overflow-x-auto pb-1">
        <select value={days} onChange={(e) => setFilter('days', e.target.value)} className={filterClass}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        {!focused ? (
          <>
            <select value={source} onChange={(e) => setFilter('source', e.target.value)} className={filterClass}>
              <option value="">All sources</option>
              <option value="transactional">Transactional</option>
              <option value="campaign">Campaign</option>
              <option value="inbox">Inbox</option>
              <option value="ivr">IVR</option>
            </select>
            <select
              value={recipientType}
              onChange={(e) => setFilter('recipientType', e.target.value)}
              className={filterClass}
            >
              <option value="">All audiences</option>
              <option value="user">Users</option>
              <option value="vendor">Service providers</option>
              <option value="phone">Phone / IVR</option>
            </select>
            <select
              value={messageCategory}
              onChange={(e) => setFilter('messageCategory', e.target.value)}
              className={filterClass}
            >
              <option value="">All categories</option>
              <option value="UTILITY">Utility</option>
              <option value="MARKETING">Marketing</option>
              <option value="AUTHENTICATION">Authentication</option>
            </select>
            <select
              value={eventType}
              onChange={(e) => setFilter('eventType', e.target.value)}
              className={filterClass}
            >
              <option value="">All events</option>
              {eventOptions.map((ev) => (
                <option key={ev} value={ev}>
                  {ev}
                </option>
              ))}
            </select>
            <select
              value={templateName}
              onChange={(e) => setFilter('templateName', e.target.value)}
              className={filterClass}
            >
              <option value="">All templates</option>
              {templateOptions.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </>
        ) : (
          <select
            value={recipientType}
            onChange={(e) => setFilter('recipientType', e.target.value)}
            className={filterClass}
          >
            <option value="">All audiences</option>
            <option value="user">Users</option>
            <option value="vendor">Service providers</option>
            <option value="phone">Phone / IVR</option>
          </select>
        )}
        <button
          type="button"
          onClick={() => setRatesOpen(true)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
          title="Meta / Interakt rates"
        >
          <Info className="h-4 w-4" />
          Rates
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading analytics…</p>
      ) : !data ? (
        <p className="text-sm text-gray-500">No analytics data</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
            <StatCard label="Billable messages" value={data.billableMessages ?? data.totals.sent} />
            <StatCard label="Est. cost" value={formatInr(data.estimatedCostInr)} sub={`${days} day period`} />
            <StatCard
              label="Delivered"
              value={data.totals.delivered}
              sub={`${data.deliveryRate}% delivery rate`}
            />
            <StatCard label="Read" value={data.totals.read} sub={`${data.readRate}% read rate`} />
            <StatCard label="Failed" value={data.totals.failed} />
            <StatCard label="Unique contacts" value={data.uniqueContacts} />
            {!eventType ? <StatCard label="Inbound replies" value={data.totals.inbound} /> : null}
          </div>

          {chartData.length > 0 ? (
            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                {eventType ? 'Daily usage for this notification' : 'Daily usage & cost'}
              </h3>
              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `₹${v}`}
                    />
                    <Tooltip
                      formatter={(value: number, name: string) =>
                        name === 'cost' ? formatInr(value) : value
                      }
                      labelFormatter={(_, payload) => payload?.[0]?.payload?.fullDay || ''}
                    />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="messages"
                      name="Messages"
                      fill="#25D366"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cost"
                      name="Est. cost"
                      stroke="#0f766e"
                      strokeWidth={2}
                      dot={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <div className="mt-8">
            <div className="mb-3 flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-gray-700 dark:bg-gray-800/60">
              {(
                [
                  { id: 'events' as const, label: eventType ? 'This notification' : 'By notification event' },
                  { id: 'templates' as const, label: eventType ? 'Template' : 'By template' },
                  ...(!eventType
                    ? [{ id: 'campaigns' as const, label: 'Recent campaigns' }]
                    : []),
                ] as Array<{ id: BreakdownTab; label: string }>
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setBreakdownTab(tab.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    breakdownTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-900 dark:text-white'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {breakdownTab === 'events' ? (
              focusedEventRows.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Event</th>
                        <th className="px-4 py-2 text-left font-medium">Template</th>
                        <th className="px-4 py-2 text-left font-medium">Audience</th>
                        <th className="px-4 py-2 text-left font-medium">Category</th>
                        <th className="px-4 py-2 text-left font-medium">Sent</th>
                        <th className="px-4 py-2 text-left font-medium">Failed</th>
                        <th className="px-4 py-2 text-left font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {focusedEventRows.map((row) => (
                        <tr
                          key={`${row.eventType}-${row.templateName}-${row.recipientType}`}
                          className="border-t border-gray-100 dark:border-gray-800"
                        >
                          <td className="px-4 py-2 font-mono text-xs">{row.eventType || '—'}</td>
                          <td className="px-4 py-2">{row.templateName || '—'}</td>
                          <td className="px-4 py-2 capitalize">{row.recipientType || '—'}</td>
                          <td className="px-4 py-2">{row.messageCategory || '—'}</td>
                          <td className="px-4 py-2">{row.sent + row.delivered + row.read}</td>
                          <td className="px-4 py-2">{row.failed}</td>
                          <td className="px-4 py-2">{formatInr(row.estimatedCostInr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No notification event data</p>
              )
            ) : null}

            {breakdownTab === 'templates' ? (
              focusedTemplateRows.length > 0 ? (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Template</th>
                        <th className="px-4 py-2 text-left font-medium">Category</th>
                        <th className="px-4 py-2 text-left font-medium">Total</th>
                        <th className="px-4 py-2 text-left font-medium">Billable</th>
                        <th className="px-4 py-2 text-left font-medium">Failed</th>
                        <th className="px-4 py-2 text-left font-medium">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {focusedTemplateRows.map((row) => (
                        <tr
                          key={`${row.templateName}-${row.messageCategory}`}
                          className="border-t border-gray-100 dark:border-gray-800"
                        >
                          <td className="px-4 py-2 font-medium">{row.templateName || '—'}</td>
                          <td className="px-4 py-2">{row.messageCategory || '—'}</td>
                          <td className="px-4 py-2">{row.total}</td>
                          <td className="px-4 py-2">{row.sent + row.delivered + row.read}</td>
                          <td className="px-4 py-2">{row.failed}</td>
                          <td className="px-4 py-2">{formatInr(row.estimatedCostInr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No template data</p>
              )
            ) : null}

            {breakdownTab === 'campaigns' && !eventType ? (
              data.recentCampaigns.length === 0 ? (
                <p className="text-sm text-gray-500">No campaigns in this period</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800/80">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium">Campaign</th>
                        <th className="px-4 py-2 text-left font-medium">Status</th>
                        <th className="px-4 py-2 text-left font-medium">Sent</th>
                        <th className="px-4 py-2 text-left font-medium">Delivered</th>
                        <th className="px-4 py-2 text-left font-medium">Read</th>
                        <th className="px-4 py-2 text-left font-medium">Failed</th>
                        <th className="px-4 py-2 text-left font-medium">Est. cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recentCampaigns.map((c) => (
                        <tr key={c.id} className="border-t border-gray-100 dark:border-gray-800">
                          <td className="px-4 py-2 font-medium">{c.name}</td>
                          <td className="px-4 py-2">{c.status}</td>
                          <td className="px-4 py-2">
                            {c.totalSent}/{c.recipientCount}
                          </td>
                          <td className="px-4 py-2">{c.totalDelivered}</td>
                          <td className="px-4 py-2">{c.totalRead}</td>
                          <td className="px-4 py-2">{c.totalFailed}</td>
                          <td className="px-4 py-2">{formatInr(c.estimatedCostInr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : null}
          </div>
        </>
      )}

      {ratesOpen ? (
        <>
          <button
            type="button"
            aria-label="Close rates"
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setRatesOpen(false)}
          />
          <aside className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">Meta / Interakt rates</h3>
                <p className="mt-0.5 text-xs text-gray-500">Estimated cost per billable template (INR)</p>
              </div>
              <button
                type="button"
                onClick={() => setRatesOpen(false)}
                className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-5">
              {[
                { key: 'UTILITY', label: 'Utility', hint: 'Transactional / utility templates' },
                { key: 'MARKETING', label: 'Marketing', hint: 'Promotional / marketing templates' },
                { key: 'AUTHENTICATION', label: 'Authentication', hint: 'OTP / auth templates' },
                { key: 'SERVICE', label: 'Service', hint: 'Service conversation messages' },
              ].map((row) => (
                <div
                  key={row.key}
                  className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{row.label}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{row.hint}</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {formatInr(rates[row.key])}
                    </p>
                  </div>
                </div>
              ))}
              <p className="text-xs leading-relaxed text-gray-500">
                Rates are estimates used for cost analytics in this hub. Update backend pricing constants when
                Meta / Interakt pricing changes.
              </p>
            </div>
          </aside>
        </>
      ) : null}
    </WhatsAppPanelShell>
  );
};

export default WhatsAppAnalyticsPanel;
